import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Check, Sparkles, Mail } from "lucide-react";
import { api } from "../../lib/api";
import { ensureFonts, FONT_AR, FONT_DISPLAY, visitorId } from "../../lib/sevenTheme";
import { BUNDLES } from "../../data/sevenNights";
import CheckoutTrustBlock from "../../components/CheckoutTrustBlock";
import PaymentHelpChatbot from "../../components/PaymentHelpChatbot";
import InlinePayment from "../../components/InlinePayment";
import InAppBrowserBanner from "../../components/InAppBrowserBanner";
import { localisedPrice } from "../../lib/localisedPrice";

const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());

// Shared pixel helper — same shape as LandingSeven so TikTok dashboards aggregate
// AddToCart / InitiateCheckout / Purchase consistently per bundle. AED price
// normalised to USD (×0.27 — AED pegged ~3.67 to USD) — TikTok benchmark is USD only.
const ttFire = (eventName, bundleKey, opts = {}) => {
  const b = BUNDLES.find((x) => x.key === bundleKey) || BUNDLES[2];
  const usdValue = Math.round(b.price * 0.27 * 100) / 100;
  const payload = {
    contents: [{
      content_id: `7nights_${b.key}`,
      content_type: "product",
      content_name: `7 ليالي بيننا — ${b.label}`,
      quantity: 1,
      price: usdValue,
    }],
    value: usdValue,
    currency: "USD",
  };
  // Browser pixel
  try {
    if (typeof window !== "undefined" && window.ttq?.track) {
      window.ttq.track(eventName, payload, opts.eventId ? { event_id: opts.eventId } : undefined);
    }
  } catch (_) { /* analytics never breaks the funnel */ }
  // Server-side postback (deduped by event_id) — strengthens iOS/cookie-blocked match rates
  if (opts.eventId && opts.s2s !== false) {
    try {
      api.post("/tiktok/event", { event_name: eventName, event_id: opts.eventId, ...payload, email: opts.email || undefined });
    } catch (_) { /* analytics never breaks the funnel */ }
  }
};

const newEid = (kind) => `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function CheckoutSeven() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const bundleParam = params.get("bundle") || "complete";
  const [bundleKey, setBundleKey] = useState(BUNDLES.find((b) => b.key === bundleParam) ? bundleParam : "complete");
  const [err, setErr] = useState("");

  const bundle = useMemo(() => BUNDLES.find((b) => b.key === bundleKey), [bundleKey]);
  const [email, setEmail] = useState("");
  // Imperative handle to the help-bot — clicking the trust-block prompt
  // opens it; if missing (no Gulf IP) the trust-block hides the prompt.
  const [chatOpen, setChatOpen] = useState(false);

  // Inline Stripe Payment Element state — we mint a fresh PaymentIntent per
  // bundle change (and once on mount). Stripe auto-renders Apple Pay / Google
  // Pay / Card from the same client_secret based on the visitor's device.
  const [clientSecret, setClientSecret] = useState(null);
  const [orderToken, setOrderToken] = useState(null);
  const [piLoading, setPiLoading] = useState(false);
  // Visitor's country (from IP geo) — passed as Stripe defaultCountry so
  // the billing-country dropdown doesn't pre-select "Egypt" by default.
  // Falls back to AE since the funnel is UAE-targeted.
  const [country, setCountry] = useState("AE");
  useEffect(() => {
    let cancelled = false;
    api.get("/geo")
      .then((r) => { if (!cancelled) {
        const c = (r.data?.country || "").toUpperCase();
        if (c && c.length === 2) setCountry(c);
      } })
      .catch(() => { /* keep AE default */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    ensureFonts();
    document.title = "7 ليالي بيننا — إتمام الشراء";
    // TikTok InitiateCheckout (browser + S2S deduped by shared event_id).
    ttFire("InitiateCheckout", bundleKey, { eventId: newEid("initcheckout") });
    // Google Ads — Add To Cart conversion (shared across funnels).
    try {
      if (window.gtag) window.gtag("event", "conversion", {
        send_to: "AW-18239642780/L2brCPLJhL8cEJy5q_lD",
        value: 1.0, currency: "EGP",
      });
    } catch (_) { /* never breaks */ }
  }, [bundleKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Mint a fresh PaymentIntent whenever the chosen bundle or typed email
  // changes. Email is debounced 800ms so we don't burn a PI on every keystroke.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setPiLoading(true);
      setErr("");
      try {
        const cleanEmail = (email || "").trim().toLowerCase();
        const payload = { bundle: bundleKey, visitor_id: visitorId() };
        if (cleanEmail && isValidEmail(cleanEmail)) payload.email = cleanEmail;
        const { data } = await api.post("/seven/payment-intent", payload);
        if (cancelled) return;
        setClientSecret(data.client_secret);
        setOrderToken(data.order_token);
      } catch (e2) {
        if (cancelled) return;
        const raw = e2?.response?.data?.detail;
        const status = e2?.response?.status;
        const safe = typeof raw === "string" && raw.length > 0 && raw.length < 160 && !/<[a-z!]/i.test(raw);
        const fallback = status && status >= 500
          ? "حدث خلل مؤقت في تحضير الدفع. حاولي مرة أخرى بعد لحظات."
          : "تعذّر تحضير نموذج الدفع. حاولي مرة أخرى.";
        setErr(safe ? raw : fallback);
      } finally {
        if (!cancelled) setPiLoading(false);
      }
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [bundleKey, email]);   // eslint-disable-line react-hooks/exhaustive-deps

  const selectBundle = (k) => {
    if (k === bundleKey) return;
    setBundleKey(k);
    // Bundle change = fresh intent → fire AddToCart for the newly chosen tier.
    ttFire("AddToCart", k, { eventId: newEid("addtocart") });
  };

  // Fire the just-before-confirm AddToCart pixel — sister to InitiateCheckout.
  // We use it in `beforeConfirm` of the InlinePayment so we capture intent
  // even if the customer drops off in 3DS / wallet flows.
  const fireSubmitPixel = () => {
    ttFire("AddToCart", bundleKey, { eventId: newEid("addtocart-submit") });
  };

  return (
    <div dir="rtl" lang="ar" className="min-h-screen bg-[#0A0606] text-[#F5EAD3]" style={{ fontFamily: FONT_AR }} data-testid="seven-checkout-root">
      <InAppBrowserBanner />
      <nav className="sticky top-0 z-30 bg-[#0A0606]/85 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-5xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/7days")} data-testid="seven-checkout-back" className="flex items-center gap-2 text-[13px] text-[#CFC3A1] hover:text-[#C9A66A] transition-colors">
            <ArrowLeft className="w-4 h-4 rotate-180" /> العودة
          </button>
          <Link to="/7days" className="flex items-center gap-2">
            <img src="/images/7nights-logo.png" alt="" className="h-8 w-8 object-contain" />
            <div className="text-[13.5px] font-extrabold text-white" style={{ fontFamily: FONT_DISPLAY }}>7 ليالي بيننا</div>
          </Link>
        </div>
      </nav>

      <main className="px-5 py-10 sm:py-14">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-start">
          {/* Form */}
          <div className="bg-[#1C1010]/60 border border-[#C9A66A]/18 rounded-2xl p-6 sm:p-8" data-testid="seven-checkout-form">
            <div className="text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold mb-2">إتمام الشراء</div>
            <h1 className="font-extrabold text-[24px] sm:text-[30px] leading-[1.3] text-white" style={{ fontFamily: FONT_DISPLAY }}>
              تجربتك تفتح <span className="text-[#C9A66A]">فورًا بعد الدفع.</span>
            </h1>
            <p className="mt-4 text-[14px] text-[#CFC3A1] leading-[1.95]">
              بعد إتمام الدفع، تنفتح لكِ لوحة التجربة كاملة على هذا الموقع مباشرة — <span className="text-white font-semibold">دون الحاجة لانتظار أي بريد أو رابط خارجي</span>. كل المحتوى يبدأ من الجوال خطوة بخطوة.
            </p>

            <div className="mt-6 bg-[#0A0606]/60 border border-[#C9A66A]/22 rounded-xl p-4" data-testid="seven-checkout-delivery-note">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#C9A66A] shrink-0 mt-0.5" />
                <div className="text-[13px] text-[#E7DDC0] leading-[1.85]">
                  <div className="font-bold text-white mb-1">طريقة الدخول</div>
                  لا حاجة لإنشاء حساب. التجربة تفتح فورًا بعد الدفع على هذا الموقع — أنتِ فقط أدخلي بريدكِ في صفحة الدفع لاستلام إيصال آمن.
                </div>
              </div>
            </div>

            {/* Optional email — when filled, pre-fills + locks the Stripe
                Checkout email field. When empty, Stripe shows a normal
                editable email field (mandatory by Stripe). */}
            <div className="mt-6" data-testid="seven-checkout-email-block">
              <label htmlFor="seven-checkout-email" className="text-[13px] text-[#E7DDC0] font-bold mb-2 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-[#C9A66A]" />
                بريدكِ الإلكتروني <span className="text-[#A7956E] font-normal">(اختياري — للإيصال)</span>
              </label>
              <input
                id="seven-checkout-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                dir="ltr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                data-testid="seven-checkout-email-input"
                className="w-full rounded-xl bg-[#0A0606] border border-white/[0.10] focus:border-[#C9A66A]/60 focus:outline-none text-[14px] text-white placeholder-[#5A5340] px-4 py-3.5 transition-colors"
              />
              <div className="mt-1.5 text-[11px] text-[#A7956E]">إذا تركتيه فارغًا، Stripe بيطلبه منكِ مرة وحدة في صفحة الدفع.</div>
            </div>

            {/* Pre-Stripe trust block + Gulf payment-help bot prompt */}
            <CheckoutTrustBlock
              theme="gold"
              testid="seven-trust-block"
              onChatOpen={() => setChatOpen(true)}
            />

            {/* Bundle selector */}
            <div className="mt-7">
              <div className="text-[13px] text-[#E7DDC0] font-bold mb-2.5">الباقة المختارة</div>
              <div className="grid gap-2">
                {BUNDLES.map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => selectBundle(b.key)}
                    data-testid={`seven-checkout-select-${b.key}`}
                    className={`text-right rounded-lg px-4 py-3 border transition-all flex items-center justify-between gap-3 ${
                      bundleKey === b.key
                        ? "bg-[#C9A66A]/12 border-[#C9A66A]/55 text-white"
                        : "bg-[#0A0606] border-white/[0.10] text-[#CFC3A1] hover:border-[#C9A66A]/35"
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <span className={`w-4 h-4 rounded-full border-2 ${bundleKey === b.key ? "bg-[#C9A66A] border-[#C9A66A]" : "border-[#5A5340]"} flex items-center justify-center`}>
                        {bundleKey === b.key && <Check className="w-2.5 h-2.5 text-[#0A0606]" strokeWidth={3.5} />}
                      </span>
                      <span className="text-[13.5px] font-bold">{b.label}</span>
                    </span>
                    <span className="flex items-baseline gap-1.5 tabular-nums" dir="ltr">
                      {b.price_original && (
                        <span className="text-[10.5px] text-[#5A5340] line-through font-bold">{b.price_original}</span>
                      )}
                      <span className="text-[13px] font-extrabold text-[#C9A66A]">{b.price} AED</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={undefined}
              disabled
              style={{ display: "none" }}
              data-testid="seven-checkout-pay-btn-legacy"
              aria-hidden="true"
            />
            {/* Inline Stripe Payment Element — replaces the legacy redirect-to-
                Stripe flow. Customer stays on this branded page and Stripe
                renders Apple Pay / Google Pay / Card automatically. */}
            <div className="mt-7" data-testid="seven-checkout-inline-payment-wrapper">
              <InlinePayment
                clientSecret={clientSecret}
                orderToken={orderToken}
                amount={bundle.price}
                currency="AED"
                currencyLabel="درهم"
                returnPath={orderToken ? `/7days/success/${orderToken}` : "/7days"}
                theme="gold"
                testid="seven-inline-payment"
                buttonLabel={`ادفع الآن — ${bundle.price} درهم`}
                beforeConfirm={fireSubmitPixel}
                defaultCountry={country}
              />
            </div>
            {(err || piLoading) && (
              <div className="mt-3 text-[13px] text-center" data-testid="seven-checkout-err">
                {err ? <span className="text-[#F0838F]">{err}</span> : <span className="text-[#A7956E]">جاري تحضير الدفع…</span>}
              </div>
            )}
            <div className="mt-5 flex items-center justify-center gap-4 text-[11.5px] text-[#A7956E]">
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3 text-[#C9A66A]" /> دفع آمن عبر Stripe</span>
              <span>·</span><span>دفعة واحدة</span>
            </div>
          </div>

          {/* Summary */}
          <aside className="lg:sticky lg:top-24 space-y-4">
            <div className="bg-gradient-to-b from-[#2A1414] to-[#150C0C] border border-[#C9A66A]/40 rounded-2xl p-6 shadow-[0_30px_80px_-25px_rgba(201,166,106,0.4)]" data-testid="seven-checkout-summary">
              <div className="text-[10.5px] tracking-[0.26em] text-[#C9A66A] font-bold">ملخّص الطلب</div>
              <div className="mt-3 flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-[17px] text-white" style={{ fontFamily: FONT_DISPLAY }}>7 ليالي بيننا</div>
                  <div className="text-[12.5px] text-[#A7956E] mt-0.5">{bundle.label}</div>
                </div>
                <div className="text-left shrink-0" dir="ltr">
                  {bundle.price_original && (
                    <div className="text-[11.5px] text-[#7A6F50] line-through tabular-nums font-bold mb-0.5" data-testid="seven-checkout-price-original">{bundle.price_original} AED</div>
                  )}
                  <div className="text-[24px] font-extrabold text-white tabular-nums">{bundle.price}</div>
                  <div className="text-[11px] text-[#C9A66A] font-bold">AED</div>
                  {bundle.price_original && (
                    <div className="mt-1 inline-block text-[9.5px] tracking-[0.16em] font-extrabold text-[#0A0606] px-1.5 py-0.5 rounded-full bg-gradient-to-b from-[#E2C281] to-[#C9A66A]" data-testid="seven-checkout-save-badge">
                      وفّري {bundle.price_original - bundle.price}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-5 border-t border-white/[0.07] pt-4 space-y-2.5 text-[13.5px] text-[#E7DDC0]">
                {bundle.items.map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 text-[#C9A66A] shrink-0" strokeWidth={2.5} />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 border-t border-white/[0.07] pt-4 flex items-center justify-between text-[15px] font-bold">
                <span className="text-[#CFC3A1]">المجموع</span>
                <span className="text-white" dir="ltr">{bundle.price} AED{(() => {
                  const lp = localisedPrice(bundle.price, "AED", country);
                  return lp ? <span className="text-[#A7956E] text-[12px] font-normal ml-2">{lp}</span> : null;
                })()}</span>
              </div>
            </div>
            <div className="text-center text-[11.5px] text-[#A7956E] leading-relaxed">
              بمتابعتك أنتِ توافقين على شروط الاستخدام وسياسة الخصوصية. منتج تعليمي خاص للأزواج، ليس استشارة زوجية أو علاج نفسي.
            </div>
          </aside>
        </div>
      </main>

      {/* Payment-help chatbot — floating bottom-left. Open-state controlled by
          the trust block's "افتحي المحادثة" CTA OR by tapping the green FAB. */}
      <PaymentHelpChatbot
        funnel="seven"
        bundleKey={bundleKey}
        getPayload={() => ({ email: ((email || "").trim().toLowerCase()) || undefined, visitor_id: visitorId() })}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
      />
    </div>
  );
}

